/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function handleZapietReload(event) {
  console.log(`Event triggered: ${event.type}`);

   // Check if the current page is the /cart page
   if (window.location.pathname !== '/cart') {
    return; // Exit the function if not on the cart page
  }

  // Check if Zapiet widget exists
  if (typeof Zapiet !== 'undefined' && Zapiet.Widget) {
      document.dispatchEvent(new CustomEvent("zapiet:start"));
  } else {
      console.warn("Zapiet widget is not available.");
  }
}

// Add event listeners for the desired events
document.addEventListener('on:line-item:change', handleZapietReload);
document.addEventListener('on:cart:add', handleZapietReload);


var attachToModels = ( modals ) =>{

    modals.forEach(function (trigger) {
      trigger.addEventListener("click", function (event) {
        event.preventDefault();
        const modal = document.getElementById(trigger.dataset.modalOffer);
        modal.classList.add("open");
        const exits = modal.querySelectorAll(".modal-exit");
        exits.forEach(function (exit) {
          exit.addEventListener("click", function (event) {
            event.preventDefault();
            modal.classList.remove("open");
          });
        });
      });
    });

  }

window.addEventListener('load', function () {

	// Special offers modal
const modals = document.querySelectorAll("[data-modal-offer]");

if(modals){

  attachToModels(modals);

}


function validateQuestionnaire(){

  // get all the mandoatory questions and check thier state
  var questions = document.querySelectorAll(".mandatory_answer");
  var questionArray = [];

  questions.forEach((q) => {

    if(!q.checked){
      questionArray.push("unchecked")
    }

  });

  if(questionArray.includes("unchecked")){
    return false;
  }else{
    return true;
  }

 }


// Over The Counter
const otcWarningEl = document.querySelector('#otc-warning');

if (otcWarningEl) {

  otcWarningEl.addEventListener('change', function () {
    if (this.checked) {
      var allQuestionsChecked = validateQuestionnaire();

      if(allQuestionsChecked){
        document.querySelector(".product-info__add-to-cart").classList.remove("otc-hidden");
        document.querySelector(".alert_msg_answer").style.display = "none";
      }else{
        document.querySelector(".alert_msg_answer").style.display = "block";
        this.checked = false;
      }

    } else {
      document.querySelector(".product-info__add-to-cart").classList.add("otc-hidden");
      document.querySelector(".alert_msg_answer").style.display = "none";
    }
  });

}


// Hide Zapiet pickup option

if(typeof hideZapietPickupOption !== 'undefined'){
  if (hideZapietPickupOption == true) {
    this.document.querySelector("#storePickupApp .checkoutMethodsContainer.default .checkoutMethod:last-child").style.display = 'none';
  }
}

});

document.addEventListener('DOMContentLoaded', function() {


const pharmacyContent = document.querySelectorAll('.pharmacy_main_service');
  const tab = document.querySelectorAll('.pharmacy_main_head');
  const tab_content = document.querySelector('.pharmacy_slider');

  function hideTabContent() {
    pharmacyContent.forEach(item => {
      item.style.display = 'none';
    });
    tab.forEach(item => {
      item.classList.remove('active');
    });
  }

  function showTabContent(i = 0) {
    pharmacyContent[i].style.display = 'block';
    tab[i].classList.add('active');
  }

  if (tab_content) {
    hideTabContent();
    showTabContent();

    tab_content.addEventListener("click", (e) => {
      const target = e.target;
      if (target) {
      	console.log("clicked buttom done");
        tab.forEach((item, i) => {
          if (target == item) {
            hideTabContent();
            showTabContent(i);
          }
        });
      }
    });
  }


   const sliders = document.querySelectorAll('.slider');

if (sliders && window.innerWidth < 820) {  // Check if the viewport width is less than 768px (mobile devices)
  sliders.forEach(slider => {
    const sliderWrapper = slider.querySelector('.slider__wrapper');
    const sliderButtons = slider.querySelectorAll('.tabs__button');
    const prevButton = slider.querySelector('.slider__prev');
    const nextButton = slider.querySelector('.slider__next');

    if (sliderButtons.length > 0) {  // Ensure there are slider buttons
      let currentIndex = 0;
      const slideWidth = sliderButtons[0].offsetWidth + 20; // button width + margin

      function updateSliderPosition() {
        sliderWrapper.style.transform = `translateX(${-currentIndex * slideWidth}px)`;
      }

      prevButton.addEventListener('click', () => {
        if (currentIndex > 0) {
          currentIndex--;
          updateSliderPosition();
        }
      });

      nextButton.addEventListener('click', () => {
        if (currentIndex < sliderButtons.length - 1) {
          currentIndex++;
          updateSliderPosition();
        }
      });
    }
  });
}



// Select all buttons with the class 'js-quick-add'
// A delay of 5 secs is added if on the PDP as the Recommended section takes an age to load due to Shopify
// faffing about server-side pulling in the recommendations.

window.onload = function () {
  const isProductPage = window.location.pathname.includes('/products/'); // Check if the URL contains '/products/'

  if (isProductPage) {
    // Apply the 3 second delay for product pages
    setTimeout(function () {
      initializeQuickAddLogic();
    }, 3000);
  } else {
    // No delay for other pages
    initializeQuickAddLogic();
  }
};

// Define the logic for quick-add functionality
function initializeQuickAddLogic() {
  const quickAddButtons = document.querySelectorAll('.js-quick-add');

  quickAddButtons.forEach(button => {
    button.addEventListener('click', function () {
      const inMedicineAttr = this.getAttribute('data-in-medicine');
      const inMedicine = inMedicineAttr ? JSON.parse(inMedicineAttr) : null;
      console.log("Button clicked => test slideout");
      const otcWarningDiv = document.querySelector('.js-product-otc-warning');
      otcWarningDiv.style.display = 'none';

      // Check if the quick-add drawer is loaded
      const checkQuickAddDrawer = setInterval(() => {
        const quickAddDrawer = document.querySelector('#product-form-quickadd');

        if (quickAddDrawer) {
          clearInterval(checkQuickAddDrawer); // Stop checking once found
          console.log("Quick add drawer is present");

          const productFormQuickAdd = document.querySelector('.product-info__add-to-cart');
          const otcWarningCheckbox = document.querySelector('#otc-warning-slider-draw');

          // Reset the OTC warning checkbox state
          if (otcWarningCheckbox) {
            otcWarningCheckbox.checked = false; // Uncheck the checkbox by default
          }

          if (productFormQuickAdd) {
            productFormQuickAdd.style.display = 'none'; // Hide initially
          } else {
            console.log("productFormQuickAdd button not found");
          }

          if (inMedicine) {
            if (otcWarningDiv) {
              otcWarningDiv.style.display = 'block';

              // Add event listener to the OTC warning checkbox
              if (otcWarningCheckbox) {
                otcWarningCheckbox.addEventListener('change', function () {
                  productFormQuickAdd.style.display = this.checked ? 'flex' : 'none';
                });
              } else {
                console.error('OTC warning checkbox not found.');
              }
            } else {
              console.error('OTC warning div not found.');
            }
          } else {
            // Hide the OTC warning div if data-in-medicine is false
            if (otcWarningDiv) {
              otcWarningDiv.style.display = 'none';
              productFormQuickAdd.style.display = 'flex';
            }
          }
        } else {
          console.log("Waiting for the quick add drawer to load...");
        }
      }, 500); // Check every 500 milliseconds
    });
  });
}

// Scroll reviews into view
var looxReviews = document.querySelector('.loox-reviews-wrap');

if(looxReviews){

  looxReviews.addEventListener('click', function () {

    // Get the element you want to scroll to
    var reviewsTab = document.querySelector('#loox-reviews-tab');

    // Calculate position to scroll to with 150px offset
    var elementPosition = reviewsTab.getBoundingClientRect().top + window.scrollY - 60;

    // Scroll to the position
    window.scrollTo({ top: elementPosition, behavior: 'smooth' });

    // Wait for the smooth scroll to finish before simulating the click
    var scrollTimeout = setInterval(function () {
      var reachedPosition = Math.abs(window.scrollY - elementPosition) < 10;

      if (reachedPosition) {
        clearInterval(scrollTimeout);
        // Simulate a click on the #reviews-in-tab element
        reviewsTab.querySelector('summary').click();
      }
    }, 100);
  });

}


/*
// Brand Menu Feature PS 23.08.24
*/
    // Select the Brands menu link
    const brandsMenu = document.querySelector('.brands-menu');
    var brandList = document.querySelector(".brand-nav-list-container");
    const viewAllBrands = document.querySelector("a.last_brand_nav");


    // Check if both elements exist
    if (brandsMenu && brandList) {
        // Add hover event listeners
        brandsMenu.addEventListener('mouseenter', function() {
            // On hover, display the brand navigation list
            brandList.style.display = 'block';
        });

        brandsMenu.addEventListener('mouseleave', function() {
            // Optional: Hide the brand navigation list when not hovering (if needed)
            // brandList.style.display = 'none';
        });

        // Add hover event listeners for the container to keep it displayed when hovered
        brandList.addEventListener('mouseenter', function() {
            // Keep it displayed if the container itself is hovered
            brandList.style.display = 'block';
        });

        brandList.addEventListener('mouseleave', function() {
            // Hide it when the mouse leaves the container
            brandList.style.display = 'none';
        });

    }



if (brandList) {
    var brandsFilter = document.querySelector(".brands-filter");
    if (brandsFilter) {
        brandsFilter.addEventListener("mouseenter", function(event) {
            console.log("moving on button");
            brandList.classList.add("show");
        });
    }

    const navLinks = document.querySelectorAll(".nav-bar__item");
    if (navLinks.length > 0) {
        navLinks.forEach((link) => {
            link.addEventListener('mouseenter', () => {
                brandList.classList.remove("show");
            });
        });
    }

    brandList.addEventListener("mouseleave", function(event) {
        brandList.classList.remove("show");
    });
}

var topFilterLinks = document.querySelectorAll(".top-filter a.filter");

if (topFilterLinks.length > 0) {
    topFilterLinks.forEach(letter => {

        letter.addEventListener('mouseover', function(event) {

            event.preventDefault();

            let letterClicked = letter.dataset.brand;

            let filterLinks = Array.from(document.querySelectorAll('.top-filter a'));
            filterLinks.forEach(node => {
                node.classList.remove("active");
            });
            event.target.classList.add("active");

            document.querySelectorAll("ul.brands-listings li").forEach(listing => {

                // reset the view
                listing.classList.remove("show");

                // check if letterClicked equals the class name on this element and show it if confirmed
                var thisClassName = listing.className;

                if (thisClassName === letterClicked) {
                    listing.classList.add("show");
                }

            });

        });

    });
}












var brandTitlesLinks = document.querySelectorAll('.brand_titles-page li a');
let lastScrollTop = 0;

console.log("brandTitlesLinks", brandTitlesLinks);

if (brandTitlesLinks.length > 0) {

  // Add click event listener to each link to scroll with offset
  brandTitlesLinks.forEach(link => {
    link.addEventListener('click', function(event) {
      event.preventDefault(); // Prevent the default link behavior

      // Get the target element from the link's href attribute
      const targetId = link.getAttribute('href').substring(1);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        // Calculate the offset position with additional 50px
        const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - 265;

        // Scroll to the target position with offset
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth' // Smooth scrolling
        });
      }
    });
  });

}












if (brandTitlesLinks.length > 0) {

    brandTitlesLinks.forEach(anchor => {
        anchor.addEventListener('click', function(e) {
            e.preventDefault();

            // Remove 'active' class from all anchors
            document.querySelectorAll('.brand_titles-page li a').forEach(a => a.classList.remove('active'));

            // Add 'active' class to the clicked anchor
            this.classList.add('active');

            // Hide or show brand results and images based on the clicked anchor's data attribute
            let dataTitle = this.getAttribute('data-titl');

            let brandResults = document.querySelectorAll('.brand_results li');
            let brandImages = document.querySelectorAll('.brand_image span');

            if (brandResults.length > 0 && brandImages.length > 0) {
                brandResults.forEach(li => {
                    if (dataTitle !== '') {
                        if (li.getAttribute('data-res') === dataTitle) {
                            li.style.display = 'block';
                        } else {
                            li.style.display = 'none';
                        }
                    } else {
                        li.style.display = 'block';
                    }
                });

                brandImages.forEach(span => {
                    if (dataTitle !== '') {
                        if (span.getAttribute('data-res') === dataTitle) {
                            span.style.display = 'block';
                        } else {
                            span.style.display = 'none';
                        }
                    } else {
                        span.style.display = 'none';
                    }
                });

                // Show the first image span if no specific dataTitle is selected
                if (dataTitle === '') {
                    let firstSpan = document.querySelector('.brand_image span:first-child');
                    if (firstSpan) {
                        firstSpan.style.display = 'block';
                    }
                }
            }
        });
    });
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('ul'),
      notch: this.querySelector('span.notch')
    };
    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('li.disclosure__item').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  hidePanel() {
    this.elements.notch.classList.remove('show');
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    const linkElement = event.currentTarget.querySelector('a');
    this.elements.input.value = linkElement.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    this.elements.button.focus();
    this.elements.panel.toggleAttribute('hidden');
    this.elements.button.setAttribute('aria-expanded', (this.elements.button.getAttribute('aria-expanded') === 'false').toString());
    this.elements.notch.classList.toggle('show');
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }
}
customElements.define('localization-form', LocalizationForm);






class MainNavDesktop {
  constructor(element) {
    this.element = element;
    this.elements = {
      details: this.element.querySelectorAll('#main-content details'),
      firstLink: this.element.querySelectorAll('#main-content details summary a')
    }

    this.elements.details.forEach(detail => detail.addEventListener('mouseenter', this.handleDetailMouseEnter.bind(this)));
    this.elements.details.forEach(detail => detail.addEventListener('click', this.toggleDetailOpen.bind(this)));
    this.elements.firstLink.forEach(link => link.addEventListener('click', this.handleFirstLinkClick.bind(this)));

    // On click escape key, close all the details
    document.addEventListener('keyup', event => {
      if(event.key === 'Escape') {
        this.closeAllDetails()
      }
    })
  }

  isMobile() {
    return window.innerWidth < 1024;
  }

  closeAllDetails() {
    this.elements.details.forEach(detail => {
      this.closeDetail(detail)
    })
  }

  closeDetail(detail) {
    // Remove attribute open
    detail.removeAttribute('open');

    // Remove class "is-open"
    detail.classList.remove('is-open');
  }

  openDetail(detail) {
    // Add attribute open
    detail.setAttribute('open', '');

    // Add class "is-open"
    detail.classList.add('is-open');
  }

  handleDetailMouseEnter(event) {
    if(this.isMobile()) return

    this.closeAllDetails();

    this.openDetail(event?.target);
  }

  toggleDetailOpen(event) {
    if(this.isMobile()) return

    const detail = event?.target.closest('details');
    const isOpened = detail.classList.contains('is-open') && detail.hasAttribute('open')

    if(!isOpened) {
      this.openDetail(detail)
    } else {
      this.closeDetail(detail)
    }
  }

  handleFirstLinkClick(event) {
    if(this.isMobile()) return

    event.preventDefault();
  }
}

const MainNavDesktops = document.querySelectorAll('.menulist--section-mbl .main-navigation--desktop');

if(MainNavDesktops.length){
  MainNavDesktops.forEach(nav => new MainNavDesktop(nav));
}

});











class MblAdvanceGridGalleryBlock {
  constructor(element) {
    this.element = element;
    this.elements = {
      quickLinks: this.element.querySelectorAll('.gallery-block__quick-links .gallery-block__quick-link'),
      textBlocks: this.element.querySelectorAll('.gallery-block__text'),
    }

    this.elements.quickLinks.forEach(quickLink => {
      quickLink.addEventListener('click', this.onQuickLinkClick.bind(this));
    });
  }

  onQuickLinkClick(event) {
    // remove the active class from all the quick links
    this.elements.quickLinks.forEach(quickLink => {
      quickLink.classList.remove('active');
    });

    // add the active class to the clicked quick link
    event.target.classList.add('active');

    // get the index of the clicked quick link
    const index = Array.from(this.elements.quickLinks).indexOf(event.target);

    // add class hidden to all the text blocks
    this.elements.textBlocks.forEach(textBlock => {
      textBlock.classList.add('hidden');
    });

    // remove the hidden class from the text block with the same index as the clicked quick link
    this.elements.textBlocks[index].classList.remove('hidden');
  }
}

const mblAdvanceGridGalleryBlocks = document.querySelectorAll('section.cc-gallery div.gallery-block');

if (mblAdvanceGridGalleryBlocks.length) {
  mblAdvanceGridGalleryBlocks.forEach(block => {
    new MblAdvanceGridGalleryBlock(block);
  });
}


var accountEl = document.querySelector(".section.customer");

if(accountEl){

  document.addEventListener('DOMContentLoaded', function() {
    const regularOrdersButton = document.querySelector('.regular-orders');
    const subscriptionOrdersButton = document.querySelector('.subscription-orders');
    const referralButton = document.querySelector('.referral_btn');
    const shopifyOrdersDiv = document.querySelector('.shopify-orders');
    const smartrrOrdersDiv = document.querySelector('.smartrr-orders');
    const referralDiv = document.querySelector('.referral_mention_me');

    // Initialize: Show shopifyOrdersDiv and hide smartrrOrdersDiv, referralDiv
    shopifyOrdersDiv.classList.remove('hidden');
    smartrrOrdersDiv.classList.add('hidden');
  	referralDiv.classList.add('hidden');

    regularOrdersButton.addEventListener('click', function(event) {
      event.preventDefault();
      if (shopifyOrdersDiv.classList.contains('hidden')) {
        shopifyOrdersDiv.classList.remove('hidden');
        smartrrOrdersDiv.classList.add('hidden');
        referralDiv.classList.add('hidden');
        regularOrdersButton.classList.add('active');
        subscriptionOrdersButton.classList.remove('active');
        referralButton.classList.remove('active');

      }
    });

    subscriptionOrdersButton.addEventListener('click', function(event) {
      event.preventDefault();
      if (smartrrOrdersDiv.classList.contains('hidden')) {
        smartrrOrdersDiv.classList.remove('hidden');
        shopifyOrdersDiv.classList.add('hidden');
        referralDiv.classList.add('hidden');
        subscriptionOrdersButton.classList.add('active');
        referralButton.classList.remove('active');
        regularOrdersButton.classList.remove('active');
      }
    });

    referralButton.addEventListener('click', function(event) {
      event.preventDefault();
      if (referralDiv.classList.contains('hidden')) {
        referralDiv.classList.remove('hidden');
        shopifyOrdersDiv.classList.add('hidden');
        smartrrOrdersDiv.classList.add('hidden');
        referralButton.classList.add('active');
         subscriptionOrdersButton.classList.remove('active');
        regularOrdersButton.classList.remove('active');
      }



    });
  });
}


document.addEventListener('DOMContentLoaded', function() {

  // Check the location and hide the shipping bar if outside IE or GB
  async function hideAnnouncementBar() {
    try {
      const response = await fetch('/browsing_context_suggestions.json');
      if (!response.ok) {
        throw new Error('Network response issue detected');
      }

      const location = await response.json();
      const countryHandle = location?.detected_values?.country?.handle;

      if (!countryHandle) {
        throw new Error('Country information not found');
      }

      const announcementBar = document.querySelector(".announcement");
      const deliveryNoteFloating = document.querySelector(".free-shipping-notice.floating-section");
      const deliveryNoteSlideoutCart = document.querySelector(".cart-drawer__content .free-shipping-notice");

      // Only show the announcement bar if countryHandle is IE or GB
      if (countryHandle === "IE" || countryHandle === "GB") {
        console.log(`Customer is in ${countryHandle === "IE" ? "Ireland" : "the UK"}`);
        announcementBar.classList.remove("hide");
        deliveryNote.classList.remove("hide-outside-roi-uk");
        deliveryNoteSlideoutCart.classList.remove("hide-outside-roi-uk");
      }

    } catch (error) {
      console.error('Error detecting location:', error);
    }
  }

  const announcementBarHandle = document.querySelector(".announcement-bar");

  if(announcementBarHandle){
    hideAnnouncementBar();
  }





    // hide free delivery note for 2.5 mins
    let hideButton = document.querySelector(".hide-delivery-note");
    let deliveryNote = document.querySelector(".free-shipping-notice");
    let timeNow = Date.now();

    if( localStorage.getItem('delivery-note-hidden') == "true"){

      // check time diff
      let storedTime = localStorage.getItem('delivery-note-hidden-at');
      let timeDiff = timeNow - storedTime;

      console.log("Time since free delivery note was cancelled (ms): "+timeDiff);

      if(timeDiff > 150000){
        localStorage.removeItem('delivery-note-hidden');
        localStorage.removeItem('delivery-note-hidden-at');
        deliveryNote.classList.remove("hide");
      }

    }else{

      if(deliveryNote){
        deliveryNote.classList.remove("hide");
      }

    }

    if(hideButton){
      hideButton.addEventListener("click", function( event ){
        event.preventDefault();
        deliveryNote.classList.add("hide");
        localStorage.setItem('delivery-note-hidden', 'true');
        localStorage.setItem('delivery-note-hidden-at', timeNow);
      })
    }

/* Easy tab read more code */
    const observer = new MutationObserver(() => {
        const tabsContainer = document.querySelector(".easytabs-container");

        // Check if tabsContainer exists to avoid null errors
        if (!tabsContainer) return;

        const tabs = tabsContainer.querySelectorAll(".easytabs-tab-item");
        const contents = tabsContainer.querySelectorAll(".easytabs-content-item");

        tabs.forEach((tab, index) => {
            const isDescriptionTab = tab.getAttribute("data-et-handle") === "tabs-description";

            if (isDescriptionTab) {
                const contentItem = contents[index];

                // Activate the tab and its content
                tab.classList.add("easytabs-active");
                contentItem.classList.add("easytabs-active");
                contentItem.classList.add("description-tab");

                const content = contentItem.querySelector(".easytabs-content-holder");

                if (content && !content.classList.contains("processed")) {
                    content.classList.add("processed");

                    // Wrap content in fade effect
                    const fadeWrapper = document.createElement("div");
                    fadeWrapper.className = "fade-wrapper";
                    content.parentNode.insertBefore(fadeWrapper, content);
                    fadeWrapper.appendChild(content);

                    // Add "Read More" button
                    const readMoreBtn = document.createElement("button");
                    readMoreBtn.textContent = "Read More";
                    readMoreBtn.className = "read-more-btn";

                    readMoreBtn.addEventListener("click", function () {
                        fadeWrapper.classList.add("expanded");
                        readMoreBtn.style.display = "none"; // Hide button after clicking
                    });

                    // Append button after the fade wrapper
                    fadeWrapper.parentNode.appendChild(readMoreBtn);
                }
            }
        });
    });

    // Observe DOM changes in the EasyTabs container
    const targetNode = document.body;
    observer.observe(targetNode, { childList: true, subtree: true });
});
